import React, { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Container, Spinner, Table,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEdit, FaPlus } from 'react-icons/fa';
import { getSetTemplates } from '@/api/reviewSetApi';
import type { ReviewSetTemplateSummaryDto } from '@/types/reviews';

const AdminReviewSetTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<ReviewSetTemplateSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = async (includeInactive: boolean) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSetTemplates(includeInactive);
      setTemplates(data);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(showInactive);
  }, [showInactive]);

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Review Set Templates</h4>
          <div className="text-secondary small mt-1">
            Define which review templates are bundled together into an annual or periodic review set.
          </div>
        </div>
        <Link to="/admin/review-sets/templates/new">
          <Button variant="primary" size="sm">
            <FaPlus className="me-1" />
            New Set Template
          </Button>
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex align-items-center gap-2 mb-3">
        <Button
          variant="link"
          size="sm"
          className="p-0"
          onClick={() => setShowInactive((v) => !v)}
        >
          {showInactive ? 'Hide inactive' : 'Show inactive'}
        </Button>
        <Link to="/admin/review-sets" className="ms-auto small">← All Review Sets</Link>
      </div>

      <Table hover size="sm" responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Category</th>
            <th>Items</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {templates.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-secondary py-4 fst-italic">
                No set templates found.
              </td>
            </tr>
          )}
          {templates.map((t) => (
            <tr key={t.setTemplateGuid}>
              <td>
                <strong>{t.setTemplateName}</strong>
                {t.setTemplateDescription && (
                  <div className="text-secondary small">{t.setTemplateDescription}</div>
                )}
              </td>
              <td><Badge bg="secondary">{t.setTemplateCategory}</Badge></td>
              <td>{t.itemCount} review{t.itemCount !== 1 ? 's' : ''}</td>
              <td>
                {t.isActive
                  ? <Badge bg="success">Active</Badge>
                  : <Badge bg="secondary">Inactive</Badge>}
              </td>
              <td className="text-end">
                <Link to={`/admin/review-sets/templates/${t.setTemplateGuid}/edit`}>
                  <Button variant="outline-secondary" size="sm">
                    <FaEdit className="me-1" />
                    Edit
                  </Button>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default AdminReviewSetTemplates;
